import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings2, Pencil, Minus, Plus, Check, X, Timer as TimerIcon } from 'lucide-react';
import { SOUND_THEMES, playCountdownTick, playGo } from './soundThemes';

const QUICK_PRESETS = [30, 45, 60, 90, 120];

/** Timer de récupération : plein écran par défaut, minimisable en barre basse (clic n'importe où),
 * décompte sonore 5-4-3-2-1 puis "GO", et modification du temps de repos à la volée.
 * Le décompte est basé sur un horodatage de fin réel (pas un simple compteur décrémenté à chaque
 * tick) : même si le navigateur throttle/suspend le `setTimeout` en arrière-plan (onglet caché,
 * changement d'app, écran verrouillé), le temps restant est toujours recalculé correctement dès
 * que l'app redevient active — le timer ne "s'arrête" donc jamais réellement. */
export const RestTimer = ({ seconds, exerciseName, onDone, en, soundTheme, onChangeSoundTheme, onChangeRestTime, awaitingScopeChoice, applySignal, isCardio }) => {
  const endTimeRef = useRef(Date.now() + seconds * 1000);
  const notifiedRef = useRef(false);
  const onDoneRef = useRef(onDone);
  const [left, setLeft] = useState(seconds);
  const [total, setTotal] = useState(seconds);
  const [showSettings, setShowSettings] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [draftTime, setDraftTime] = useState(seconds);

  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  const recompute = useCallback(() => {
    const remaining = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
    setLeft(remaining);
    return remaining;
  }, []);

  // Boucle auto-suffisante (indépendante du re-render/état `left`) : chaque tick recalcule le
  // temps restant à partir de l'horodatage réel de fin, jamais d'un simple décrément. Ainsi, même
  // si ce `setTimeout` est throttlé/gelé pendant un moment en arrière-plan, la prochaine exécution
  // (ou le retour au premier plan via l'effet de resync ci-dessous) retombe toujours sur la bonne
  // valeur — le décompte ne "s'arrête" jamais réellement.
  useEffect(() => {
    if (editingTime || awaitingScopeChoice) return;
    let timeoutId;
    const tick = () => {
      const remaining = recompute();
      if (remaining <= 0) {
        if (!notifiedRef.current) {
          notifiedRef.current = true;
          playGo(soundTheme, isCardio);
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        }
        onDoneRef.current();
        return;
      }
      if (remaining <= 5) playCountdownTick(soundTheme, remaining, isCardio);
      timeoutId = setTimeout(tick, 1000);
    };
    tick();
    return () => clearTimeout(timeoutId);
  }, [editingTime, awaitingScopeChoice, recompute, soundTheme, isCardio]);

  // Resynchronisation immédiate dès que l'onglet/app redevient visible (le setTimeout peut avoir
  // été entièrement suspendu pendant l'arrière-plan sur mobile).
  useEffect(() => {
    const resync = () => { if (document.visibilityState === 'visible') recompute(); };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [recompute]);

  // Demande la permission de notification une seule fois (best-effort, silencieux si refusée) —
  // permet d'alerter le client même si l'app est en arrière-plan quand le repos se termine.
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Notification navigateur programmée à l'échéance exacte du repos courant.
  useEffect(() => {
    if (editingTime || awaitingScopeChoice) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const remainingMs = Math.max(0, endTimeRef.current - Date.now());
    const id = setTimeout(() => {
      try {
        new Notification(en ? 'Rest time is over 💪' : 'Temps de repos terminé 💪', {
          body: exerciseName || (en ? 'Time for your next set!' : 'C\'est reparti pour la série suivante !'),
          tag: 'bodyup-rest-timer',
        });
      } catch { /* silencieux si le navigateur refuse (ex: permission révoquée en cours de route) */ }
    }, remainingMs);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTime, awaitingScopeChoice, total]);

  // Applique le nouveau temps confirmé uniquement une fois la portée résolue (pas d'annulation = pas de changement).
  useEffect(() => {
    if (applySignal) {
      endTimeRef.current = Date.now() + applySignal.seconds * 1000;
      notifiedRef.current = false;
      setTotal(applySignal.seconds);
      recompute();
    }
  }, [applySignal, recompute]);

  const stop = (e) => e.stopPropagation();

  const openEditor = (e) => {
    stop(e);
    setDraftTime(left);
    setEditingTime(true);
  };

  const applyDraftTime = (val) => setDraftTime(Math.max(5, Math.min(900, val)));

  const confirmTimeChange = (e) => {
    stop(e);
    setEditingTime(false);
    onChangeRestTime && onChangeRestTime(draftTime);
  };

  const progressPct = Math.max(0, Math.min(100, (left / total) * 100));

  if (minimized) {
    return (
      <motion.div
        initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
        role="button" tabIndex={0}
        onClick={() => setMinimized(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMinimized(false); }}
        className="fixed bottom-0 inset-x-0 z-[100] bg-zinc-950/95 backdrop-blur-md border-t border-primary/30 flex items-center gap-3 px-5 py-3 text-left cursor-pointer"
        data-testid="rest-timer-minibar"
      >
        <div className="absolute top-0 left-0 h-0.5 bg-primary transition-[width]" style={{ width: `${progressPct}%` }} />
        <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
          <TimerIcon className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold truncate">{exerciseName || (en ? 'Rest' : 'Récupération')}</p>
          <p className="text-zinc-500 text-[11px]">{en ? 'Tap to expand' : 'Toucher pour agrandir'}</p>
        </div>
        <span className={`text-2xl font-black tabular-nums ${left <= 5 ? 'text-primary' : 'text-white'}`} data-testid="rest-timer-minibar-value">
          {left > 0 ? left : (en ? 'GO!' : 'GO !')}
        </span>
        <button onClick={(e) => { stop(e); onDone(); }} className="text-zinc-500 hover:text-white text-xs font-semibold px-2" data-testid="skip-rest-btn-mini">
          {en ? 'Skip' : 'Passer'}
        </button>
      </motion.div>
    );
  }

  return (
    <div onClick={() => setMinimized(true)} className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex flex-col items-center justify-center" data-testid="rest-timer-overlay">
      <button onClick={openEditor} className="absolute top-6 left-6 text-zinc-500 hover:text-white" data-testid="rest-timer-edit-btn">
        <Pencil className="w-5 h-5" />
      </button>
      <button onClick={(e) => { stop(e); setShowSettings((v) => !v); }} className="absolute top-6 right-6 text-zinc-500 hover:text-white" data-testid="rest-timer-settings-btn">
        <Settings2 className="w-5 h-5" />
      </button>
      {showSettings && (
        <div onClick={stop} className="absolute top-16 right-6 bg-zinc-900 border border-white/10 rounded-xl p-2 space-y-1" data-testid="sound-theme-picker">
          {SOUND_THEMES.map((t) => (
            <button
              key={t.value}
              onClick={(e) => { stop(e); onChangeSoundTheme(t.value); setShowSettings(false); }}
              className={`block w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold ${soundTheme === t.value ? 'bg-primary text-white' : 'text-zinc-400 hover:bg-white/5'}`}
              data-testid={`sound-theme-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      {exerciseName && <p className="text-white font-bold text-sm mb-1">{exerciseName}</p>}
      <p className="text-zinc-400 text-sm font-bold uppercase tracking-widest mb-4">{en ? 'Rest' : 'Récupération'}</p>

      <AnimatePresence mode="wait">
        {editingTime ? (
          <motion.div key="editor" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} onClick={stop} className="flex flex-col items-center gap-5" data-testid="rest-timer-editor">
            <div className="flex items-center gap-4">
              <button onClick={() => applyDraftTime(draftTime - 15)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-testid="rest-timer-minus-15">
                <Minus className="w-5 h-5" />
              </button>
              <span className="text-6xl font-black text-white tabular-nums w-32 text-center" data-testid="rest-timer-draft-value">{draftTime}s</span>
              <button onClick={() => applyDraftTime(draftTime + 15)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-testid="rest-timer-plus-15">
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {QUICK_PRESETS.map((p) => (
                <button
                  key={p} onClick={() => applyDraftTime(p)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${draftTime === p ? 'bg-primary text-white' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}
                  data-testid={`rest-timer-preset-${p}`}
                >
                  {p}s
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-1">
              <button onClick={(e) => { stop(e); setEditingTime(false); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/20 text-zinc-400 hover:text-white text-sm font-semibold transition-colors" data-testid="rest-timer-cancel-edit">
                <X className="w-4 h-4" /> {en ? 'Cancel' : 'Annuler'}
              </button>
              <button onClick={confirmTimeChange} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold transition-transform hover:scale-105" data-testid="rest-timer-confirm-edit">
                <Check className="w-4 h-4" /> {en ? 'Confirm' : 'Valider'}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="countdown" className="flex flex-col items-center">
            <AnimatePresence mode="popLayout">
              <motion.div key={left} initial={{ scale: 1.3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }} className={`text-8xl sm:text-9xl font-black ${left <= 5 ? 'text-primary' : 'text-white'}`} data-testid="rest-timer-value">
                {left > 0 ? left : (en ? 'GO!' : 'GO !')}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {!editingTime && (
        <button onClick={(e) => { stop(e); onDone(); }} className="mt-10 px-5 py-2.5 rounded-full border border-white/20 text-zinc-400 hover:text-white hover:border-white/40 text-sm font-semibold transition-colors" data-testid="skip-rest-btn">
          {en ? 'Skip' : 'Passer'}
        </button>
      )}
    </div>
  );
};

export const Confetti = () => (
  <div className="fixed inset-0 z-[110] pointer-events-none overflow-hidden">
    {Array.from({ length: 40 }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ y: -20, x: `${Math.random() * 100}vw`, opacity: 1, rotate: 0 }}
        animate={{ y: '110vh', rotate: 360 * (Math.random() > 0.5 ? 1 : -1) }}
        transition={{ duration: 2.2 + Math.random() * 1.5, ease: 'linear', delay: Math.random() * 0.4 }}
        className="absolute w-2.5 h-2.5 rounded-sm"
        style={{ backgroundColor: ['#ec4899', '#a855f7', '#22d3ee', '#facc15', '#22c55e'][i % 5] }}
      />
    ))}
  </div>
);
